import {
    autoUpdate,
    flip,
    offset,
    shift,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react-dom-interactions";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import Typography from "./Typography";

export type TooltipProps = {
    text: string | number;
    children: React.ReactNode;
};

const Tooltip = ({ text, children }: TooltipProps) => {
    const [open, setOpen] = useState(false);

    const { x, y, reference, floating, strategy, context } = useFloating({
        placement: "top",
        open,
        onOpenChange: setOpen,
        middleware: [offset(15), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context, { restMs: 40 }),
        useFocus(context),
        useRole(context, { role: "tooltip" }),
        useDismiss(context),
    ]);

    return (
        <>
            <span ref={reference} {...getReferenceProps()}>
                {children}
            </span>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            type: "spring",
                            damping: 20,
                            stiffness: 400,
                        }}
                        ref={floating}
                        className="bg-white text-black pointer-events-none rounded-md p-2 shadow-xl font-normal whitespace-normal z-50"
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                        }}
                        {...getFloatingProps()}
                    >
                        <Typography
                            variant="body-medium"
                            component="p"
                            className="text-lg"
                        >
                            {text}
                        </Typography>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Tooltip;
